import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Heart, Users } from "lucide-react";

import { listMyMeetupEvents, type MyMeetupEvent } from "@/lib/api/participants";
import { useAuthUser } from "@/lib/use-auth-user";

import { Loader, ErrorBlock } from "@/components/ui/States";
import { DashboardTopbar } from "@/components/layout/DashboardTopbar";
import { DashboardSidebar } from "@/components/layout/DashboardSidebar";

function fullDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function MyMatchesPage() {
  const { user } = useAuthUser();

  const { data: meetups, isLoading, error } = useQuery({
    queryKey: ["my-meetup-events", user?.id],
    queryFn: listMyMeetupEvents,
    enabled: Boolean(user),
  });

  const withMatches = useMemo(
    () => (meetups ?? []).filter((m) => m.my_match_count > 0),
    [meetups],
  );

  const totalMatches = useMemo(
    () => withMatches.reduce((s, m) => s + m.my_match_count, 0),
    [withMatches],
  );

  return (
    <div className="min-h-screen bg-[#F0F2F5]">
      <DashboardTopbar />

      <div className="md:grid md:min-h-[calc(100vh-64px)] md:grid-cols-[260px_1fr_300px]">
        <DashboardSidebar active="matches" />

        <main className="min-w-0 px-5 py-8 pb-24 md:px-10 md:py-10 md:pb-10">
          <header className="mb-8">
            <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">My matches</h1>
            <p className="mt-1 text-[14px] text-[#6c757d]">
              {totalMatches > 0
                ? `${totalMatches} ${totalMatches === 1 ? "match" : "matches"} across all your events`
                : "Mutual matches from all events you attended"}
            </p>
          </header>

          {isLoading ? (
            <Loader label="Loading matches..." />
          ) : error ? (
            <ErrorBlock
              title="Couldn't load"
              message={error instanceof Error ? error.message : String(error)}
            />
          ) : withMatches.length === 0 ? (
            <EmptyMatches />
          ) : (
            <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(320px,1fr))]">
              {withMatches.map((m) => (
                <MatchEventCard key={m.event_id} m={m} />
              ))}
            </div>
          )}
        </main>

        <aside className="hidden border-l border-[#e2e8f0] bg-white px-6 py-8 md:block" />
      </div>
    </div>
  );
}

function MatchEventCard({ m }: { m: MyMeetupEvent }) {
  return (
    <Link
      to={`/e/${m.slug}/matches`}
      className="flex flex-col overflow-hidden rounded-[20px] border border-[#e2e8f0] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.04)] transition hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)]"
    >
      <div className="px-6 pt-6">
        <h3 className="mb-1.5 text-[18px] font-bold leading-tight tracking-tight text-[#1a1a1a]">
          {m.title}
        </h3>
        <div className="mb-4 flex flex-col gap-1.5 text-[13px] text-[#475569]">
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-[#94a3b8]" />
            <span>{fullDate(m.event_date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-3.5 w-3.5 text-[#94a3b8]" />
            <span>{m.participant_count} {m.participant_count === 1 ? "participant" : "participants"}</span>
          </div>
        </div>
      </div>

      <div className="mx-6 mb-4 rounded-[8px] border border-[#a7f3d0] bg-[#f0fdf4] px-3.5 py-2.5 text-[12px] text-[#065f46]">
        <p className="text-[11px] font-bold uppercase tracking-wide">
          {m.my_match_count} {m.my_match_count === 1 ? "match" : "matches"}
        </p>
        <p className="mt-0.5">Tap to view contacts</p>
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-[#e2e8f0] px-6 py-4 text-[13px] font-semibold">
        <span className="inline-flex items-center gap-1.5 text-[#065f46]">
          <Heart className="h-3.5 w-3.5" />
          View matches
        </span>
        <span className="text-[#94a3b8]">→</span>
      </div>
    </Link>
  );
}

function EmptyMatches() {
  return (
    <div className="rounded-[20px] border border-dashed border-[#e2e8f0] bg-white px-6 py-16 text-center">
      <Heart className="mx-auto mb-3 h-10 w-10 text-[#cbd5e1]" />
      <p className="text-[15px] font-semibold text-[#475569]">No matches yet</p>
      <p className="mt-1 text-[13px] text-[#94a3b8]">
        After you attend an event with a recorded pitch and people like each other back,<br className="hidden sm:inline" />
        the matches will show up here.
      </p>
    </div>
  );
}
